import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import { BACKEND_URL } from '../lib/api';

const API = `${BACKEND_URL}/api`;

// Raw fetch (bypasses axios) so we capture the exact browser-level error.
async function probe(label, url, opts) {
  const t0 = Date.now();
  try {
    const res = await fetch(url, opts);
    let bodyPreview = '';
    try { bodyPreview = (await res.text()).slice(0, 120); } catch { /* ignore */ }
    return { label, url, ok: res.ok, status: res.status, ms: Date.now() - t0,
      acao: res.headers.get('access-control-allow-origin'), body: bodyPreview };
  } catch (e) {
    return { label, url, ok: false, status: 'FETCH_FAILED', ms: Date.now() - t0,
      error: `${e.name}: ${e.message}` };
  }
}

export default function Debug() {
  const nav = useNavigate();
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);

  const env = {
    REACT_APP_BACKEND_URL: BACKEND_URL || '(undefined)',
    page_origin: window.location.origin,
    same_origin: BACKEND_URL ? (new URL(BACKEND_URL).origin === window.location.origin) : 'n/a',
    userAgent: navigator.userAgent,
    cookieEnabled: navigator.cookieEnabled,
    has_session_cookie: document.cookie.includes('session_token'),
    cookie_raw: document.cookie || '(empty)',
    sw_controller: !!(navigator.serviceWorker && navigator.serviceWorker.controller),
  };

  const run = async () => {
    setRunning(true); setResults(null); setCopied(false);
    const tests = [];
    tests.push(await probe('GET /api/ (root, +cred)', `${API}/`, { credentials: 'include' }));
    tests.push(await probe('GET /circles/circle_demo01 (+cred)', `${API}/circles/circle_demo01`, { credentials: 'include' }));
    tests.push(await probe('GET /circles/circle_demo01 (NO cred)', `${API}/circles/circle_demo01`, { credentials: 'omit' }));
    tests.push(await probe('GET /circles (list, +cred)', `${API}/circles`, { credentials: 'include' }));
    let swRegs = 0;
    try { swRegs = (await navigator.serviceWorker?.getRegistrations())?.length || 0; } catch { /* ignore */ }
    setResults({ generated_at: new Date().toISOString(), env: { ...env, sw_registrations: swRegs }, tests });
    setRunning(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(results, null, 2));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const nuke = async () => {
    try {
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } finally {
      window.location.reload();
    }
  };

  const box = { background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 12, padding: 12, marginBottom: 10 };
  const btn = (bg, color) => ({ background: bg, color, border: bg === 'transparent' ? `1px solid ${PS.line}` : 'none', borderRadius: 999, padding: '12px 16px', fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI, cursor: 'pointer', width: '100%' });

  return (
    <div style={{ minHeight: '100dvh', background: PS.page, color: PS.text, fontFamily: PS.fontUI, padding: '18px 16px 40px', maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button data-testid="debug-back" onClick={() => nav('/app')} style={{ border: 'none', background: PS.panel, width: 36, height: 36, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.chevronL size={18} stroke={PS.ink} />
        </button>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Peershare · Debug</div>
      </div>

      <div style={{ ...box }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: PS.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Environment</div>
        <pre data-testid="debug-env" style={{ margin: 0, fontSize: 11, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: PS.text }}>{JSON.stringify(env, null, 2)}</pre>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        <button data-testid="debug-run" onClick={run} disabled={running} style={btn(PS.ink, PS.page)}>
          {running ? 'กำลังทดสอบ…' : 'รันทดสอบการเชื่อมต่อ API'}
        </button>
        {results && (
          <button data-testid="debug-copy" onClick={copy} style={btn(PS.terracotta, '#fff')}>
            {copied ? 'คัดลอกแล้ว ✓ — วางส่งให้ทีมได้เลย' : 'คัดลอกผลทั้งหมด'}
          </button>
        )}
        <button data-testid="debug-nuke" onClick={nuke} style={btn('transparent', PS.text)}>
          ล้าง Cache + Service Worker แล้วรีโหลด
        </button>
      </div>

      {results && (
        <div style={{ ...box }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: PS.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>ผลทดสอบ API</div>
          {results.tests.map((t, i) => (
            <div key={i} data-testid={`debug-result-${i}`} style={{ borderTop: i ? `1px solid ${PS.line}` : 'none', paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{t.label}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.ok ? PS.forest : PS.danger, marginTop: 2 }}>
                {t.ok ? `✓ ${t.status}` : `✕ ${t.status}`}{typeof t.ms === 'number' ? ` · ${t.ms}ms` : ''}
              </div>
              {t.error && <div style={{ fontSize: 11.5, color: PS.danger, marginTop: 3, wordBreak: 'break-all' }}>{t.error}</div>}
              {t.acao !== undefined && <div style={{ fontSize: 10.5, color: PS.textMuted, marginTop: 2 }}>ACAO: {String(t.acao)}</div>}
              {t.body && <div style={{ fontSize: 10.5, color: PS.textDim, marginTop: 2, wordBreak: 'break-all' }}>{t.body}</div>}
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, color: PS.textDim, lineHeight: 1.6, marginTop: 8 }}>
        วิธีใช้: เปิดหน้านี้บนมือถือที่มีปัญหา → กด "รันทดสอบ" → กด "คัดลอกผลทั้งหมด" → วางส่งกลับมาให้ทีม
      </div>
    </div>
  );
}
